import React, { useState } from "react";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";

interface User {
  id: string;
  name: string;
  email: string;
}

interface Ad {
  id: string;
  title: string;
  isCountryWide: boolean;
  ufs: Array<{
    id: number;
    name: string;
    abbreviation: string;
  }>;
  cities: Array<{
    id: string;
    name: string;
  }>;
}

// ⚠️ CORREÇÃO 1: Adicionar '| null' à propriedade 'ad'
interface Payment {
  paymentId: string;
  financialRegister: string | null;
  status: string;
  amount: string;
  updatedAt: string;
  user: User;
  ad: Ad | null;
}

interface FinanceiroCardProps {
  payment: Payment;
}

const FinanceiroCard: React.FC<FinanceiroCardProps> = ({ payment }) => {
  // Função para formatar a data e hora de forma legível
  const formatDateAndTime = (date: string) => {
    const dateObj = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // Para exibir no formato 24h
    };
    return dateObj.toLocaleString("pt-BR", options);
  };

  function capitalizeWord(word: string) {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  function capitalizeSentence(sentence: string) {
    return sentence.split(" ").map(capitalizeWord).join(" ");
  }

  // ⚠️ TRATAMENTO DE ERRO: Caso o 'ad' seja null, não renderiza o card ou renderiza uma mensagem.
  // Como o componente pai já está filtrando payments.ad !== null no useEffect,
  // esta verificação é uma camada de segurança. No entanto, se o componente pai
  // não fosse filtrar (o que é mais comum), ela seria obrigatória.
  // Se você deseja garantir que o card SEMPRE renderize, mas a informação do anúncio seja opcional:

  // ⚠️ Crie uma variável auxiliar para o anúncio
  const ad = payment.ad;

  return (
    <div>
      <Grid>
        <Card>
          <CardContent
            sx={{
              position: "relative",
              paddingTop: "30px",
            }}
          >
            <Box
              sx={{
                display: "block",
                position: "absolute",
                top: "4px",
                right: "8px",
                backgroundColor: "#cfe9ff",
                color: "#001120",
                padding: "4px 8px",
                borderRadius: "4px",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "12px",
              }}
            >
              {payment.financialRegister}
            </Box>
            <Typography variant="h6" component="div">
              <strong>Status:</strong>{" "}
              {payment.status === "COMPLETED" ? "CONCLUÍDO" : payment.status}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              <strong>Valor:</strong> R$ {parseFloat(payment.amount).toFixed(2)}
            </Typography>

            {/* Informações do Usuário e Data */}
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Nome: </strong>
                {payment.user.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>E-mail: </strong>
                {payment.user.email}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Data e Hora: </strong>
                {formatDateAndTime(payment.updatedAt)}{" "}
              </Typography>
            </Box>

            {/* ⚠️ CORREÇÃO 2: Renderização Condicional de Detalhes do Anúncio */}
            {/* Só renderiza o bloco de detalhes do anúncio se 'ad' não for null */}
            {ad && (
              <Box mt={2}>
                {/* Nome do Anúncio */}
                {ad.title && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Nome do Anúncio: </strong>
                    {ad.title}
                  </Typography>
                )}

                {/* Veiculação Nacional */}
                {ad.isCountryWide && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Região que o anúncio será veiculado:</strong>
                    </Typography>
                    <Box
                      sx={{
                        // ... estilos existentes
                        display: "inline-block",
                        backgroundColor: "#d9ead3",
                        color: "#274e13",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        marginRight: "4px",
                        marginBottom: "4px",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      <strong>Brasil</strong>
                    </Box>
                  </Box>
                )}

                {/* Veiculação por Estado */}
                {ad.ufs && ad.ufs.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Anúncio será veiculado no estado:</strong>
                    </Typography>
                    {ad.ufs.map((uf, index) => (
                      <Box
                        key={index}
                        sx={{
                          // ... estilos existentes
                          display: "inline-block",
                          backgroundColor: "#fff2cc",
                          color: "#7f6000",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          marginRight: "4px",
                          marginBottom: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        <strong>{uf.name}</strong>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Veiculação por Cidade */}
                {ad.cities && ad.cities.length > 0 && (
                  <Box>
                    {ad.cities.length > 1 ? (
                      <Typography variant="body2" color="textSecondary">
                        <strong>O anúncio será veiculado nas cidades:</strong>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        <strong>O anúncio será veiculado na cidade:</strong>
                      </Typography>
                    )}

                    {ad.cities.map((citie, index) => (
                      <Box
                        key={index}
                        sx={{
                          // ... estilos existentes
                          display: "inline-block",
                          backgroundColor: "#c9daf8",
                          color: "#073763",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          marginRight: "4px",
                          marginBottom: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {capitalizeSentence(citie.name)}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Se o 'ad' for null, podemos opcionalmente mostrar uma mensagem */}
            {!ad && (
              <Box mt={2}>
                <Typography variant="body2" color="error">
                  Informações do anúncio indisponíveis.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </div>
  );
};

export default FinanceiroCard;
